import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { GetPendingMembershipsAPI, PendingMembership } from "@/services/api/society/getPendingMemberships";
import { ApproveMembershipAPI } from "@/services/api/society/approveMembership";
import Icon from "@/components/Icon";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminStatusPill from "@/components/AdminStatusPill";
import FailReasonModal from "@/components/FailReasonModal";
import Modal from "@/components/Modal";

/**
 * Society membership approval screen (admin). Lists pending membership
 * requests for a society and lets the admin approve or reject them.
 */
const SocietyMembersPage: React.FC = () => {
  const { t } = useTranslation();
  const { societyId } = useParams<{ societyId: string }>();
  const [searchParams] = useSearchParams();
  const societyName = searchParams.get("name") || "";

  const [memberships, setMemberships] = useState<PendingMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failModalVisible, setFailModalVisible] = useState(false);
  const [rejectingMembershipId, setRejectingMembershipId] = useState<string | null>(null);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approvingMembershipId, setApprovingMembershipId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPendingMemberships = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    setError(null);
    const resp = await GetPendingMembershipsAPI(societyId);
    if (Array.isArray(resp)) {
      setMemberships(resp);
    } else {
      setError("Failed to fetch pending memberships");
    }
    setLoading(false);
  }, [societyId]);

  useEffect(() => {
    fetchPendingMemberships();
  }, [fetchPendingMemberships]);

  const handleApprove = async (membershipId: string) => {
    setActionLoading(true);
    try {
      const resp = await ApproveMembershipAPI({
        membership_id: membershipId,
        approved: true,
      });
      if (resp.status === 0) {
        await fetchPendingMemberships();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPress = (membershipId: string) => {
    setRejectingMembershipId(membershipId);
    setFailModalVisible(true);
  };

  const handleRejectSubmit = async (reason: string) => {
    if (!rejectingMembershipId) return;
    setActionLoading(true);
    try {
      const resp = await ApproveMembershipAPI({
        membership_id: rejectingMembershipId,
        approved: false,
        reject_reason: reason,
      });
      if (resp.status === 0) {
        setFailModalVisible(false);
        setRejectingMembershipId(null);
        await fetchPendingMemberships();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprovePress = (membershipId: string) => {
    setApprovingMembershipId(membershipId);
    setApproveModalVisible(true);
  };

  const handleApproveConfirm = async () => {
    if (!approvingMembershipId) return;
    setApproveModalVisible(false);
    const id = approvingMembershipId;
    setApprovingMembershipId(null);
    await handleApprove(id);
  };

  return (
    <div className="flex min-h-full flex-col p-4">
      <h1 className="mb-4 text-2xl font-bold text-[#222]">
        {societyName ? `${societyName} - ${t("Pending")}` : t("Pending Memberships")}
      </h1>

      {loading ? (
        <AdminListSkeleton count={3} />
      ) : error ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="cloud-alert-outline"
          title={t("Something went wrong")}
          message={error}
          actionLabel={t("Retry")}
          onAction={fetchPendingMemberships}
        />
      ) : memberships.length === 0 ? (
        <AdminEmptyState
          iconType="MaterialCommunityIcons"
          iconName="account-check-outline"
          title={t("No pending membership requests")}
          message={t("Pull to refresh to check for new requests.")}
          actionLabel={t("Refresh")}
          onAction={fetchPendingMemberships}
        />
      ) : (
        <div className="w-full">
          {memberships.map((membership) => {
            const student = membership.student || {};
            return (
              <div key={membership.id} className="mb-4 rounded-xl bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center">
                  {student.profile_icon ? (
                    <img
                      src={student.profile_icon}
                      alt=""
                      className="mr-3 h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F5F5F5]">
                      <Icon
                        iconType="MaterialCommunityIcons"
                        name="account-circle"
                        size={44}
                        color="#bbb"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold text-[#222]">
                      {student.university_email || student.id}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-[#666]">
                      {student.university || "-"}
                    </div>
                    <div className="mt-0.5 text-xs text-[#666]">
                      {`${t("Requested")}: ${
                        membership.requested_at
                          ? new Date(membership.requested_at).toLocaleDateString()
                          : "-"
                      }`}
                    </div>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  <AdminStatusPill
                    label={`${t("Student Card")}: ${
                      student.student_card_number ? t("Uploaded") : t("Not uploaded")
                    }`}
                    tone={student.student_card_number ? "success" : "neutral"}
                    iconType="MaterialCommunityIcons"
                    iconName={student.student_card_number ? "check-circle" : "alert-circle-outline"}
                    iconSize={12}
                  />
                  <AdminStatusPill
                    label={`${t("HKID")}: ${
                      student.hkid_number ? t("Uploaded") : t("Not uploaded")
                    }`}
                    tone={student.hkid_number ? "success" : "neutral"}
                    iconType="MaterialCommunityIcons"
                    iconName={student.hkid_number ? "check-circle" : "alert-circle-outline"}
                    iconSize={12}
                  />
                </div>

                {membership.approved_by && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <AdminStatusPill
                      label={t("Approved by member - awaiting admin")}
                      tone="warning"
                      iconType="MaterialCommunityIcons"
                      iconName="clock-outline"
                      iconSize={12}
                    />
                  </div>
                )}

                <div className="mt-3.5 flex justify-end gap-2.5">
                  <button
                    type="button"
                    className={`flex items-center gap-1 rounded-lg bg-[#4caf50] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 ${
                      actionLoading ? "opacity-50" : ""
                    }`}
                    onClick={() => handleApprovePress(membership.id)}
                    disabled={actionLoading}
                  >
                    <Icon iconType="MaterialCommunityIcons" name="check" size={16} color="#fff" />
                    {t("Approve")}
                  </button>
                  <button
                    type="button"
                    className={`flex items-center gap-1 rounded-lg bg-[#f44336] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 ${
                      actionLoading ? "opacity-50" : ""
                    }`}
                    onClick={() => handleRejectPress(membership.id)}
                    disabled={actionLoading}
                  >
                    <Icon iconType="MaterialCommunityIcons" name="close" size={16} color="#fff" />
                    {t("Reject")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve confirm modal */}
      <Modal visible={approveModalVisible} onClose={() => setApproveModalVisible(false)}>
        <div className="w-[85%] max-w-[360px] rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#e8f5e9]">
            <Icon iconType="MaterialCommunityIcons" name="check" size={28} color="#4caf50" />
          </div>
          <div className="mb-1 text-base font-semibold text-[#222]">{t("Approve")}</div>
          <div className="mb-3.5 text-[13px] text-[#888]">
            {t("Approve this membership request?")}
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#f5f5f5] py-3 text-sm font-semibold text-[#666]"
              onClick={() => setApproveModalVisible(false)}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="flex-1 rounded-[10px] bg-[#4caf50] py-3 text-sm font-semibold text-white"
              onClick={handleApproveConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? "..." : t("Approve")}
            </button>
          </div>
        </div>
      </Modal>

      <FailReasonModal
        visible={failModalVisible}
        onClose={() => {
          setFailModalVisible(false);
          setRejectingMembershipId(null);
        }}
        loading={actionLoading}
        title={t("Reject Membership Request")}
        onSubmit={handleRejectSubmit}
      />
    </div>
  );
};

export default SocietyMembersPage;